import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Task } from '../../../types';
import { type TaskDetailLevel } from '../../hooks/useBoardTaskDetailLevel';
import { DetailLevelSubmenu } from './DetailLevelSubmenu';

interface TaskContextMenuProps {
  x: number;
  y: number;
  task: Task;
  splitDate?: string;
  onAssign: () => void;
  onDuplicate: () => void;
  onSplit?: () => void;
  onRemoveFromBoard: () => void;
  onDelete: () => void;
  onClose: () => void;
  detailLevel?: TaskDetailLevel;
  detailMode?: 'auto' | 'manual';
  onSetDetailLevel?: (level: TaskDetailLevel) => void;
  onSetDetailAutoMode?: () => void;
}

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  color: '#1f2937',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  textAlign: 'left',
};

const deleteItemStyle: React.CSSProperties = {
  ...menuItemStyle,
  color: '#dc2626',
};

const separatorStyle: React.CSSProperties = {
  height: 1,
  backgroundColor: '#e5e7eb',
  margin: '4px 0',
};

function MenuItem({
  onClick,
  style,
  hoverBg,
  children,
  onMouseEnter,
  onMouseLeave,
}: {
  onClick: () => void;
  style: React.CSSProperties;
  hoverBg?: string;
  children: React.ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => {
        setHovered(true);
        onMouseEnter?.();
      }}
      onMouseLeave={() => {
        setHovered(false);
        onMouseLeave?.();
      }}
      style={{
        ...style,
        backgroundColor: hovered ? (hoverBg || '#f3f4f6') : 'transparent',
      }}
    >
      {children}
    </button>
  );
}

function formatDate(dateStr: string): string {
  const [y, m, d] = dateStr.split('-');
  return `${d}.${m}.${y}`;
}

export const TaskContextMenu = memo(function TaskContextMenu({
  x,
  y,
  task: _task,
  splitDate,
  onAssign,
  onDuplicate,
  onSplit,
  onRemoveFromBoard,
  onDelete,
  onClose,
  detailLevel,
  detailMode,
  onSetDetailLevel,
  onSetDetailAutoMode,
}: TaskContextMenuProps) {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);
  const [showDetailSubmenu, setShowDetailSubmenu] = useState(false);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Adjust position to stay within viewport
  const adjustedX = Math.min(x, window.innerWidth - 200);
  const adjustedY = Math.min(y, window.innerHeight - 350);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  const hasDetailProps = detailLevel !== undefined && onSetDetailLevel && onSetDetailAutoMode;

  const handleDetailItemEnter = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    showTimerRef.current = setTimeout(() => setShowDetailSubmenu(true), 50);
  };

  const handleDetailItemLeave = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    hideTimerRef.current = setTimeout(() => setShowDetailSubmenu(false), 150);
  };

  const handleSubmenuEnter = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const handleSubmenuLeave = () => {
    hideTimerRef.current = setTimeout(() => setShowDetailSubmenu(false), 150);
  };

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: adjustedX,
        top: adjustedY,
        zIndex: 9999,
        backgroundColor: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        padding: 4,
        minWidth: 180,
      }}
    >
      <MenuItem onClick={onAssign} style={menuItemStyle}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <line x1="19" y1="8" x2="19" y2="14" />
          <line x1="22" y1="11" x2="16" y2="11" />
        </svg>
        <span style={{ flex: 1 }}>{t('boardv3.contextMenu.assign')}</span>
        <span style={{ color: '#9ca3af', fontSize: 16 }}>&rsaquo;</span>
      </MenuItem>

      <div style={separatorStyle} />

      <MenuItem onClick={onDuplicate} style={menuItemStyle}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
        <span>{t('boardv3.contextMenu.duplicate')}</span>
      </MenuItem>

      {onSplit && splitDate && (
        <MenuItem onClick={onSplit} style={{ ...menuItemStyle, color: '#7c3aed' }} hoverBg="#f5f3ff">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="22" />
            <polyline points="8 6 12 2 16 6" />
            <polyline points="8 18 12 22 16 18" />
          </svg>
          <span>{t('boardv3.contextMenu.splitAt', { date: formatDate(splitDate) })}</span>
        </MenuItem>
      )}

      <MenuItem
        onClick={onRemoveFromBoard}
        style={{
          ...menuItemStyle,
          color: '#ea580c',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ea580c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
        </svg>
        <span>{t('boardv3.contextMenu.removeFromBoard')}</span>
      </MenuItem>

      {hasDetailProps && (
        <>
          <div style={separatorStyle} />

          <div
            style={{ position: 'relative' }}
            onMouseEnter={handleDetailItemEnter}
            onMouseLeave={handleDetailItemLeave}
          >
            <DetailTriggerItem />

            {showDetailSubmenu && (
              <DetailLevelSubmenu
                detailLevel={detailLevel!}
                detailMode={detailMode!}
                parentX={adjustedX}
                onSetDetailLevel={onSetDetailLevel!}
                onSetDetailAutoMode={onSetDetailAutoMode!}
                onMouseEnter={handleSubmenuEnter}
                onMouseLeave={handleSubmenuLeave}
              />
            )}
          </div>
        </>
      )}

      <div style={separatorStyle} />

      <MenuItem onClick={onDelete} style={deleteItemStyle} hoverBg="#fef2f2">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
        <span>{t('boardv3.contextMenu.delete')}</span>
      </MenuItem>
    </div>
  );
});

function DetailTriggerItem() {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...menuItemStyle,
        backgroundColor: hovered ? '#f3f4f6' : 'transparent',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="8" x2="21" y2="8" />
        <line x1="3" y1="16" x2="21" y2="16" />
        <line x1="3" y1="12" x2="15" y2="12" />
      </svg>
      <span style={{ flex: 1 }}>{t('boardv3.contextMenu.detailLevel')}</span>
      <span style={{ color: '#9ca3af', fontSize: 16 }}>&rsaquo;</span>
    </button>
  );
}

